import * as Sentry from '@sentry/react';
import axios, { AxiosError } from 'axios';

function safeJson(v: unknown) {
  try {
    return typeof v === 'string'
      ? maybeTruncate(v)
      : JSON.parse(JSON.stringify(v));
  } catch {
    return String(v);
  }
}
function maybeTruncate(s: string, max = 1000) {
  return s.length > max ? s.slice(0, max) + '…(truncated)' : s;
}

export function normalizeError(e: unknown): Error {
  if (e instanceof Error) return e;

  if (axios.isAxiosError(e)) {
    const ax = e as AxiosError<any>;
    let msg = ax.message || 'Axios request error';

    if (ax.response) {
      const st = ax.response.status;
      const stText = ax.response.statusText;
      const responseData = ax.response.data as any;
      const dataMsg = responseData?.message ?? responseData?.error;
      msg = `HTTP ${st ?? '—'}${stText ? ` ${stText}` : ''}${dataMsg ? `: ${dataMsg}` : ''}`;
    } else if (ax.request) {
      msg = 'No response received from server';
    }

    const m = ax.config?.method?.toUpperCase();
    const u = ax.config?.url;
    if (m && u) msg = `[${m} ${u}] ${msg}`;

    const err = new Error(msg);
    err.name = 'AxiosError';
    // SentryのlinkedErrorsIntegrationでAxiosエラーの詳細な因果チェーンを表示
    (err as any).cause = ax;
    return err;
  }

  // 非Axiosのプレーン値
  let msg: string;
  try {
    msg = typeof e === 'string' ? e : JSON.stringify(e);
  } catch {
    msg = String(e);
  }
  return new Error(msg);
}

export function sentryReportError(
  originalErr: unknown,
  enrich?: (scope: Sentry.Scope) => void,
): void {
  const error = normalizeError(originalErr);

  Sentry.withScope((scope) => {
    // 呼び出し側の文脈（code/state等）を注入
    if (enrich) enrich(scope);

    // ここで一元的に enrich（タグ/コンテキスト/extra）
    if (axios.isAxiosError(originalErr)) {
      const ax = originalErr as AxiosError<any>;

      // Sentry: Tags
      if (ax.response?.status)
        scope.setTag('http.status_code', String(ax.response.status));
      if (ax.config?.method)
        scope.setTag('http.method', String(ax.config.method).toUpperCase());
      scope.setTag('error.kind', 'AxiosError');

      // Sentry: Contexts
      scope.setContext('request', {
        url: ax.config?.url,
        baseURL: ax.config?.baseURL,
        method: ax.config?.method,
        params: ax.config?.params,
        data: safeJson(ax.config?.data),
      });

      scope.setContext('response', {
        status: ax.response?.status,
        statusText: ax.response?.statusText,
        data: safeJson(ax.response?.data),
      });
    }

    // Sentry: Additional Data
    scope.setExtra('original', safeJson(originalErr));
    Sentry.captureException(error);
  });
}
